import { STORAGE_KEYS } from '../common/constants';
import { env } from '../common/env';

interface RequestConfig {
    headers?: Record<string, string>;
    [key: string]: any;
}

class AjaxService {
    private baseURL = env.apiBaseUrl;

    private getToken(): string | null {
        return sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    }

    private async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        const token = this.getToken();

        // For FormData bodies the browser must set the Content-Type itself
        // (multipart boundary), so never force application/json there.
        const isFormData = options.body instanceof FormData;

        const config: RequestInit = {
            ...options,
            headers: {
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                ...(token && { Authorization: `Bearer ${token}` }),
                ...options.headers,
            },
        };

        try {
            const response = await fetch(url, config);

            if (response.status === 401) {
                const freshToken = this.getToken();
                if (freshToken && freshToken !== token) {
                    config.headers = {
                        ...config.headers,
                        Authorization: `Bearer ${freshToken}`,
                    };
                    const retryResponse = await fetch(url, config);
                    if (retryResponse.status !== 401) {
                        return await this.handleResponse(retryResponse);
                    }
                }
                this.forceLogout();
                return await this.handleResponse(response);
            }

            return await this.handleResponse(response);
        } catch (error) {
            throw this.handleError(error);
        }
    }

    private forceLogout(): void {
        sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
        if (window.location.pathname !== '/login') {
            window.location.href = '/login?sessionExpired=1';
        }
    }

    private async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            const res = await response.json().catch(() => ({}));
            throw new Error(res.error || res.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return await response.json();
        }

        return response.text() as unknown as T;
    }

    private handleError(error: any): Error {
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return new Error('Network error: Unable to connect to server');
        }
        return error;
    }

    async get<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
        return this.request<T>(url, {
            method: 'GET',
            ...config,
        });
    }

    private serializeBody(data?: any): BodyInit | undefined {
        if (data === undefined || data === null) return undefined;
        if (data instanceof FormData) return data;
        return JSON.stringify(data);
    }

    async post<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
        return this.request<T>(url, {
            method: 'POST',
            body: this.serializeBody(data),
            ...config,
        });
    }

    async patch<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
        return this.request<T>(url, {
            method: 'PATCH',
            body: this.serializeBody(data),
            ...config,
        });
    }

    async put<T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> {
        return this.request<T>(url, {
            method: 'PUT',
            body: this.serializeBody(data),
            ...config,
        });
    }

    async delete<T = any>(url: string, config: RequestConfig = {}): Promise<T> {
        return this.request<T>(url, {
            method: 'DELETE',
            ...config,
        });
    }
}

const ajax = new AjaxService();

export default ajax;
